/**
 * Billing — TV-P4 / TV-RV-10
 *
 * TV-RV-10: front-desk read-only view.
 *   - Both provider and front-desk can reach this route (RequireRole in App.jsx
 *     allows both).
 *   - When canSeeClinical=false (front-desk), a non-destructive "Read-Only View"
 *     banner is shown at the top. No billing-submit or edit actions are rendered
 *     in this component today, so the UI is effectively read-only for both roles.
 *     The banner makes the intent explicit and future-proofs any provider-only
 *     action buttons added later.
 */
import React from 'react';
import { Card, Badge, StatCard, AIPanel, DataRow } from '@/components';
import { Clock, FileText, DollarSign, Zap, Eye } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { getViewConfig } from '@/auth/viewConfig';

const Billing = () => {
  // TV-RV-10: detect front-desk read-only mode
  const { user } = useAuth();
  const cfg = getViewConfig(user?.role ?? 'patient');
  const isReadOnly = !cfg.canSeeClinical;
  // Stat cards data
  const stats = [
    {
      icon: Clock,
      label: 'Wait Time',
      value: '12m',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      icon: Clock,
      label: 'Face-to-Face',
      value: '23:30',
      unit: 'minutes',
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      icon: FileText,
      label: 'Documentation',
      value: '5m',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
    {
      icon: DollarSign,
      label: 'Total Billable',
      value: '28:30',
      unit: 'minutes',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
    },
  ];

  // Visit timeline events
  const timelineEvents = [
    {
      time: '10:02 AM',
      event: 'Patient checked in',
      type: 'check-in',
    },
    {
      time: '10:10 AM',
      event: 'Vitals captured (BP, Weight, Temperature)',
      type: 'vitals',
    },
    {
      time: '10:15 AM',
      event: 'Face-to-face visit started with Dr. Reddy',
      type: 'visit-start',
    },
    {
      time: '10:38 AM',
      event: 'Medication reviewed and Semaglutide prescribed',
      type: 'action',
    },
    {
      time: '10:40 AM',
      event: 'Referrals generated (Ophthalmology, Dietitian)',
      type: 'action',
    },
    {
      time: '10:42 AM',
      event: 'Orders placed for labs (HbA1c, CMP)',
      type: 'action',
    },
    {
      time: '10:44 AM',
      event: 'Patient discharged and education provided',
      type: 'discharge',
    },
    {
      time: '10:47 AM',
      event: 'Clinical documentation completed',
      type: 'documentation',
    },
  ];

  // Insurance billing details
  const insuranceBilling = {
    cpt: '99214',
    pos: '02',
    modifier: '95',
    dxCodes: ['E11.65', 'I10', 'E66.01'],
    hcc: ['19', '47'],
  };

  // Provider analytics
  const providerAnalytics = [
    {
      label: 'Visits Today',
      value: '12',
      unit: 'patients',
    },
    {
      label: 'Total Billable',
      value: '4h 12m',
      unit: 'minutes',
    },
    {
      label: 'Avg Visit',
      value: '21m',
      unit: 'per patient',
    },
    {
      label: 'Daily Revenue',
      value: '$882',
      unit: 'estimated',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0d14] to-[#0f1119] p-6">
      <div className="max-w-7xl mx-auto">
        {/* TV-RV-10: read-only banner for front-desk */}
        {isReadOnly && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <Eye className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-300">
              <span className="font-semibold">Read-Only View</span> — Billing data is visible for reconciliation.
              Clinical modifications are provider-only.
            </p>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Time Tracking & Billing</h1>
          <p className="text-gray-400">Maria Garcia • Visit on Mar 21, 2026</p>
        </div>

        {/* Stat Cards Grid */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {stats.map((stat, idx) => {
            const IconComponent = stat.icon;
            return (
              <Card key={idx} className="p-4">
                <div className={`flex items-center justify-between`}>
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    {stat.unit && <p className="text-xs text-gray-500 mt-1">{stat.unit}</p>}
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <IconComponent className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Visit Timeline */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Visit Timeline</h2>

              <div className="space-y-4">
                {timelineEvents.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    {/* Timeline dot */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          item.type === 'check-in' || item.type === 'discharge'
                            ? 'bg-blue-500'
                            : item.type === 'visit-start'
                              ? 'bg-green-500'
                              : item.type === 'action'
                                ? 'bg-amber-500'
                                : 'bg-purple-500'
                        }`}
                      />
                      {idx < timelineEvents.length - 1 && (
                        <div className="w-0.5 h-8 bg-[#1e2235] my-1" />
                      )}
                    </div>

                    {/* Event content */}
                    <div className="pb-2">
                      <p className="text-xs font-mono text-gray-400">{item.time}</p>
                      <p className="text-sm text-gray-200 mt-1">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* CollabKare Platform Billing */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">CollabKare Platform Billing</h2>

              <div className="space-y-4">
                <DataRow
                  label="Face-to-Face Time"
                  value="23 min 30 sec"
                  valueColor="text-gray-100"
                />
                <DataRow
                  label="Documentation Time"
                  value="5 min"
                  valueColor="text-gray-100"
                />
                <div className="border-t border-[#1e2235] py-4">
                  <DataRow
                    label="Total Billable Time"
                    value="28 min 30 sec"
                    valueColor="text-gray-100"
                    labelColor="font-semibold"
                  />
                </div>

                <div className="bg-[#12141e] p-4 rounded-lg space-y-2 mt-4">
                  <DataRow
                    label="Billable Rate"
                    value="$3.50 / min"
                    valueColor="text-amber-400"
                  />
                  <div className="border-t border-[#1e2235] pt-3 mt-3">
                    <DataRow
                      label="Total Charge"
                      value="$99.75"
                      valueColor="text-green-400"
                      labelColor="font-semibold text-lg"
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Insurance Billing Details */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Insurance Billing Details</h2>

              <div className="space-y-4">
                {/* CPT Code */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">CPT Code</p>
                  <div className="p-3 bg-[#12141e] rounded-lg border border-[#1e2235]">
                    <p className="font-mono text-green-400">{insuranceBilling.cpt}</p>
                    <p className="text-xs text-gray-500 mt-1">Office visit, established patient, moderate-high complexity</p>
                  </div>
                </div>

                {/* Place of Service */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Place of Service (POS)</p>
                  <div className="p-3 bg-[#12141e] rounded-lg border border-[#1e2235]">
                    <p className="font-mono text-blue-400">{insuranceBilling.pos}</p>
                    <p className="text-xs text-gray-500 mt-1">Telehealth - Patient located in rural area</p>
                  </div>
                </div>

                {/* Modifier */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Modifier</p>
                  <div className="p-3 bg-[#12141e] rounded-lg border border-[#1e2235]">
                    <p className="font-mono text-purple-400">{insuranceBilling.modifier}</p>
                    <p className="text-xs text-gray-500 mt-1">Synchronous telehealth visit</p>
                  </div>
                </div>

                {/* Diagnosis Codes */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Primary Dx Codes</p>
                  <div className="space-y-2">
                    {insuranceBilling.dxCodes.map((code, idx) => (
                      <div key={idx} className="p-2 bg-[#12141e] rounded-lg border border-[#1e2235]">
                        <p className="font-mono text-amber-400 text-sm">{code}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* HCC Risk Adjustment */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">HCC Risk Adjustment</p>
                  <div className="flex flex-wrap gap-2">
                    {insuranceBilling.hcc.map((code, idx) => (
                      <Badge key={idx} className="bg-red-500/20 text-red-400">
                        HCC {code}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Provider Daily Analytics */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Provider Daily Analytics</h2>

              <div className="grid grid-cols-2 gap-4">
                {providerAnalytics.map((metric, idx) => (
                  <div key={idx} className="p-3 bg-[#12141e] rounded-lg border border-[#1e2235]">
                    <p className="text-xs font-semibold text-gray-400 mb-2">{metric.label}</p>
                    <p className="text-lg font-bold text-white">{metric.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{metric.unit}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* AI Documentation Savings */}
            <AIPanel
              title="AI Documentation Savings"
              content={
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-sm">
                    <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-200">Time Saved</p>
                      <p className="text-gray-400">5 minutes of manual documentation eliminated</p>
                    </div>
                  </div>
                  <div className="bg-[#12141e] p-3 rounded-lg border border-[#1e2235]">
                    <p className="text-xs text-gray-400 mb-2">Revenue Impact</p>
                    <p className="text-sm font-semibold text-green-400">+$17.50 per visit</p>
                    <p className="text-xs text-gray-500 mt-1">Freed time for 12 daily visits = $210/day savings</p>
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
